import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

const CONSENT_KEY = "orphion_consent";

/**
 * GDPR/DPDP-aligned consent banner. Shows once until the visitor chooses.
 * Dispatches a `orphion-consent` window event ("accepted" | "declined") so any
 * future analytics scripts can gate on it — nothing third-party loads before consent.
 */
export const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(CONSENT_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const choose = (value) => {
    try {
      localStorage.setItem(CONSENT_KEY, value);
    } catch {
      /* private mode — banner just closes */
    }
    window.dispatchEvent(new CustomEvent("orphion-consent", { detail: value }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[60] bg-[var(--bg)] border-t border-[var(--line-strong)] shadow-[0_-8px_40px_rgba(20,17,13,0.08)]"
          data-testid="consent-banner"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <p className="text-sm text-[var(--ink-80)] leading-relaxed flex-1">
              We use a few cookies to run the site and — only with your OK — to
              understand which pages help visitors. No advertising trackers, ever.
              Read our{" "}
              <Link to="/cookies" className="text-[var(--accent)] underline underline-offset-4">
                Cookie Policy
              </Link>
              .
            </p>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => choose("declined")}
                className="btn-ghost text-xs"
                data-testid="consent-decline"
              >
                <span>Decline</span>
              </button>
              <button
                onClick={() => choose("accepted")}
                className="btn-pill text-xs"
                data-testid="consent-accept"
              >
                <span>Accept</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsentBanner;
